<script setup lang="ts">
import { ONBOARDING_SCENARIO_KEY } from '../composables/useOnboardingContext'
import { useOnboardingStore } from '../stores/onboardingStore'
import { SHARED_ANCHORS } from '../scenarios/shared/anchors'

/**
 * Кнопка запуска онбординга.
 * - `toolbar` — как «Формы и отчёты» в тулбаре таблицы;
 * - `ear` — «ушко» embedded-формы;
 * - `inline` — в мобильных и не-embedded рядах кнопок формы.
 * Без `scenario` берёт сценарий ближайшей формы из useOnboardingContext(),
 * поэтому работает при любом способе открытия формы.
 */
const props = withDefaults(defineProps<{
  scenario?: string
  variant?: 'toolbar' | 'ear' | 'inline'
  /** Сторона квадратной кнопки варианта `toolbar` — под высоту соседних кнопок тулбара */
  size?: number
}>(), {
  scenario: undefined,
  variant: 'toolbar',
  size: 38,
})

// Корень — VTooltip, поэтому классы и прочие атрибуты родителя отдаём самой кнопке
defineOptions({ inheritAttrs: false })

const onboardingStore = useOnboardingStore()
const injectedScenario = inject(ONBOARDING_SCENARIO_KEY, undefined)
const scenarioId = computed(() => props.scenario ?? injectedScenario)

const isBusy = computed(() => onboardingStore.isActive || onboardingStore.isTransitioning)

// Кнопку не блокируем: во время тура она сама подсвечивается шагом и не должна выглядеть неактивной
function launch() {
  if (scenarioId.value && !isBusy.value)
    void onboardingStore.start(scenarioId.value)
}
</script>

<template>
  <VTooltip
    v-if="scenarioId"
    location="bottom"
    text="Обучение по разделу"
  >
    <template #activator="{ props: tooltipProps }">
      <VBtn
        v-bind="{ ...tooltipProps, ...$attrs }"
        icon="ri-question-line"
        :color="variant === 'ear' ? 'primary' : 'secondary'"
        :variant="variant === 'ear' ? undefined : variant === 'toolbar' ? 'outlined' : 'text'"
        :class="variant === 'ear' ? 'entity-form-action-btn' : 'flex-shrink-0'"
        :size="variant === 'toolbar' ? size : undefined"
        :rounded="variant === 'toolbar' ? 'sm' : undefined"
        :data-onboarding="variant === 'toolbar' ? SHARED_ANCHORS.toolbar.onboarding : SHARED_ANCHORS.ear.onboarding"
        aria-label="Обучение по разделу"
        @click.stop="launch"
      />
    </template>
  </VTooltip>
</template>
